// Modules, seats, prices and industry templates (Falcon Platform Build Plan).

export const MODULES = ["plm", "crm"] as const;
export type Module = (typeof MODULES)[number];
export const MODULE_LABEL: Record<Module, string> = { plm: "Falcon PLM", crm: "Falcon CRM" };

export const BILLING_STATUSES = ["Trial", "Active", "Internal", "Past due", "Suspended", "Cancelled"] as const;
export type BillingStatus = (typeof BILLING_STATUSES)[number];

export const CURRENCIES = ["GBP", "USD", "EUR", "PKR", "SAR", "AED", "CNY", "BDT", "INR", "TRY"] as const;

export interface LicenceTerms {
  plmEnabled: boolean;
  crmEnabled: boolean;
  plmSeats: number;
  crmSeats: number;
  plmPricePerUser: number;
  crmPricePerUser: number;
  bundleDiscountPercent: number;
}

export interface MonthlyCharge { plm: number; crm: number; discount: number; total: number }

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Monthly licence charge: seats × price per module, less the bundle discount when both are taken. */
export function monthlyCharge(t: LicenceTerms): MonthlyCharge {
  const plm = t.plmEnabled ? round2(t.plmSeats * t.plmPricePerUser) : 0;
  const crm = t.crmEnabled ? round2(t.crmSeats * t.crmPricePerUser) : 0;
  const bundle = t.plmEnabled && t.crmEnabled;
  const pct = bundle ? Math.min(100, Math.max(0, t.bundleDiscountPercent || 0)) : 0;
  const discount = round2(((plm + crm) * pct) / 100);
  return { plm, crm, discount, total: round2(plm + crm - discount) };
}

/** Statuses that count towards licence revenue. */
export function isBillable(status: string): boolean {
  return status === "Active" || status === "Past due";
}

export const INDUSTRY_TEMPLATES = ["workwear", "fashion", "general"] as const;
export type IndustryTemplate = (typeof INDUSTRY_TEMPLATES)[number];
export const INDUSTRY_LABEL: Record<IndustryTemplate, string> = {
  workwear: "Workwear / PPE",
  fashion: "Fashion",
  general: "General (other goods)",
};

export const FEATURE_DEFS: ReadonlyArray<readonly [string, string]> = [
  ["standards", "Standards & certification (EN, NFPA)"],
  ["catAtpvFr", "CAT / ATPV / FR speciality"],
  ["season", "Season / collection / drop"],
  ["genderFitColourways", "Gender, fit, colourways"],
  ["fabricComposition", "Fabric composition & GSM"],
  ["aql", "AQL inspections"],
];
export type Features = Record<string, boolean>;

// "Optional" in the plan starts switched off; the organisation's admin can turn it on.
const TEMPLATE_FEATURES: Record<IndustryTemplate, Features> = {
  workwear: { standards: true, catAtpvFr: true, season: false, genderFitColourways: false, fabricComposition: true, aql: true },
  fashion: { standards: false, catAtpvFr: false, season: true, genderFitColourways: true, fabricComposition: true, aql: false },
  general: { standards: false, catAtpvFr: false, season: false, genderFitColourways: false, fabricComposition: false, aql: false },
};

export function templateFeatures(t: IndustryTemplate): Features {
  return { ...TEMPLATE_FEATURES[t] };
}

export function normalizeFeatures(value: unknown, template: IndustryTemplate): Features {
  const base = templateFeatures(template);
  if (value && typeof value === "object") {
    for (const [k] of FEATURE_DEFS) {
      const v = (value as Record<string, unknown>)[k];
      if (typeof v === "boolean") base[k] = v;
    }
  }
  return base;
}
